import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Card } from "../../../components/Card";
import { InfoBox } from "../../../components/InfoBox";
import { LoadingSpinner } from "../../../components/LoadingSpinner";
import colors from "@styling/colors";
import { spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import type { DBImportJob } from "@KassaroAB/shared/types/importJob";
import { JobStatus } from "@KassaroAB/shared/types/enums/JobStatus";

const ProgressContainer = styled.div`
  margin-top: ${spacing.x4}px;
`;

const CardTitle = styled.h3`
  margin: 0 0 ${spacing.x2}px 0;
  font-size: ${fontSizes.large}px;
  font-weight: 600;
  color: ${colors.text};
`;

const CardDescription = styled.p`
  margin: 0 0 ${spacing.x3}px 0;
  max-width: 52rem;
  font-size: 14px;
  line-height: 1.55;
  color: ${colors.textMuted};
`;

const ProgressBarContainer = styled.div`
  width: 100%;
  height: 24px;
  background-color: ${colors.border};
  border-radius: 4px;
  overflow: hidden;
  margin: ${spacing.x2}px 0;
`;

const ProgressBar = styled.div<{ $progress: number }>`
  height: 100%;
  width: ${({ $progress }) => $progress}%;
  background-color: ${colors.primary};
  transition: width 0.3s ease;
`;

const ProgressText = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  color: ${colors.text};
  margin-top: ${spacing.x1}px;
`;

const StatusText = styled.div<{ $status: string }>`
  font-weight: 600;
  color: ${({ $status }) => {
    switch ($status) {
      case "completed":
        return colors.success;
      case "failed":
        return colors.danger;
      case "processing":
        return colors.primary;
      default:
        return colors.textMuted;
    }
  }};
`;

interface ImportProgressProps {
  job: DBImportJob | undefined;
  isLoading: boolean;
}

export const ImportProgress = ({ job, isLoading }: ImportProgressProps) => {
  const { t } = useTranslation();

  if (isLoading && !job) {
    return (
      <ProgressContainer>
        <Card>
          <CardTitle>{t("productImport.progressTitle")}</CardTitle>
          <CardDescription>{t("productImport.progressLoadingDescription")}</CardDescription>
          <LoadingSpinner centered />
        </Card>
      </ProgressContainer>
    );
  }

  if (!job) {
    return null;
  }

  const completedRows = job.processedRows + (job.skippedRows ?? 0);
  const progress =
    completedRows > 0 && job.totalRows > 0
      ? Math.round((completedRows / job.totalRows) * 100)
      : 0;

  const statusKey = (status: JobStatus) => {
    switch (status) {
      case JobStatus.PENDING:
        return "statusPending";
      case JobStatus.PROCESSING:
        return "statusProcessing";
      case JobStatus.COMPLETED:
        return "statusCompleted";
      case JobStatus.FAILED:
        return "statusFailed";
      default:
        return "statusPending";
    }
  };

  const statusStyle =
    job.status === JobStatus.COMPLETED
      ? "completed"
      : job.status === JobStatus.FAILED
        ? "failed"
        : job.status === JobStatus.PROCESSING
          ? "processing"
          : "pending";

  return (
    <ProgressContainer>
      <Card>
        <CardTitle>{t("productImport.progressTitle")}</CardTitle>
        <CardDescription>{t("productImport.progressDescription")}</CardDescription>
        <StatusText $status={statusStyle}>
          {t(`productImport.${statusKey(job.status)}`)}
        </StatusText>

        {(job.status === JobStatus.PROCESSING || job.status === JobStatus.COMPLETED) && (
          <>
            <ProgressBarContainer>
              <ProgressBar $progress={progress} />
            </ProgressBarContainer>
            <ProgressText>
              <span>
                {t("productImport.productsProcessed", {
                  processed: job.processedRows,
                  total: job.totalRows,
                })}
              </span>
              <span>{progress}%</span>
            </ProgressText>
            {job.processedRows > 0 && (
              <ProgressText>
                <span style={{ color: colors.success }}>
                  {t("productImport.succeeded", { count: job.processedRows })}
                </span>
              </ProgressText>
            )}
            {job.failedRows > 0 && (
              <ProgressText>
                <span style={{ color: colors.danger }}>
                  {t("productImport.failedRows", { count: job.failedRows })}
                </span>
              </ProgressText>
            )}
            {(job.skippedRows ?? 0) > 0 && (
              <ProgressText>
                <span style={{ color: colors.textMuted }}>
                  {t("productImport.skippedChainRows", { count: job.skippedRows })}
                </span>
              </ProgressText>
            )}
          </>
        )}

        {job.status === JobStatus.PENDING && (
          <InfoBox variant="info">{t("productImport.jobQueued")}</InfoBox>
        )}

        {job.status === JobStatus.COMPLETED && (
          <InfoBox variant="success">
            {t("productImport.importCompletedSuccess")}
            {job.processedRows > 0 && (
              <div style={{ marginTop: spacing.x1 }}>
                {t("productImport.productsImportedCount", { count: job.processedRows })}
              </div>
            )}
          </InfoBox>
        )}

        {job.status === JobStatus.FAILED && (
          <InfoBox variant="error">{t("productImport.importFailedShort")}</InfoBox>
        )}
      </Card>
    </ProgressContainer>
  );
};
